import { PageId } from 'constants/pages'
import { isEmpty } from 'lodash'
import { isWoofyChatUpsellAfterAccountEnabled } from 'nove8/cohorts/woofz_ppp_woofy/abtest/isWoofyChatUpsellAfterAccountEnabled'
import { PaymentSystem } from 'nove8/constants/payment'
import { TStep } from 'nove8/types'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore } from 'zustandGlobalStore'
import { getHasBasicSubscription } from '../../helpers/getHasBasicSubscription'

const getHasEmail = () => !!useZustandGlobalStore.getState().lead?.email
const getHasSubscription = () =>
  getHasBasicSubscription(
    useZustandGlobalStore.getState().lead?.subscriptions || null,
  )
const getHasAccount = () =>
  !!useZustandGlobalStore.getState().lead?.firebaseToken
const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )

const isPaypalPayment = () =>
  ((useZustandGlobalStore.getState().lead?.subscriptions?.[0]
    ?.paymentSystemType as unknown) as PaymentSystem) === PaymentSystem.PAYPAL

const shouldShowChatUpsellAfterAccount = (): boolean => {
  if (!isWoofyChatUpsellAfterAccountEnabled()) {
    return false
  }

  if (isPaypalPayment()) {
    return false
  }

  const userSubscriptions = useZustandGlobalStore.getState().lead?.subscriptions
  const hasActiveChatSubscription = userSubscriptions?.find(
    (subscription) =>
      subscription.productName === 'chat_2' &&
      !['created', 'cancelled'].includes(subscription.status),
  )

  return !hasActiveChatSubscription
}

export const getXStateConfigWoofzPppWoofy = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    always: [
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const pageIdFromUrl = window.location.pathname.split('/').pop() || ''
          return (
            !getHasEmail() &&
            !getHasSubscription() &&
            !getHasAccount() &&
            getHasAnswers() &&
            pageIdFromUrl === PageId.NEWSLETTER_URGE_WOOFZ_PPP_WOOFY
          )
        },
        target: PageId.EMAIL_WOOFZ_PPP_WOOFY,
      },
      {
        cond: (): boolean => {
          const hasEmail = getHasEmail()
          const hasSubscription = getHasSubscription()
          const hasAccount = getHasAccount()
          const hasAnswers = getHasAnswers()
          return !hasEmail && !hasSubscription && !hasAccount && hasAnswers
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        cond: (): boolean => getHasAccount(),
        target: PageId.DOWNLOAD_WOOFY_WOOFZ_PPP_WOOFY,
      },
      {
        cond: (): boolean => getHasSubscription(),
        target: PageId.ACCOUNT_WOOFY_WOOFZ_PPP_WOOFY,
      },
      {
        cond: (): boolean => getHasEmail(),
        target: PageId.SUBSCRIPTIONS_WOOFZ_PPP_WOOFY,
      },
      {
        target: pages[0].pageId,
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          SKIP_ONE_STEP: {
            target: pages[index + 2]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.EMAIL_WOOFZ_PPP_WOOFY && {
            NEXT_FLOW_STEP: [
              {
                cond: () => {
                  const url = new URL(window.location.href)
                  const languageFromUrl = url.searchParams.get('lang')
                  return languageFromUrl !== 'en'
                },
                target: PageId.LAST_CHANCE_OFFER_WOOFZ_PPP_WOOFY,
              },
              {
                target: pages[index + 1]?.pageId,
              },
            ],
          }),
          ...(page.pageId === PageId.SUBSCRIPTIONS_WOOFZ_PPP_WOOFY && {
            NEXT_FLOW_STEP: [
              {
                cond: (): boolean =>
                  isPaypalPayment() || isWoofyChatUpsellAfterAccountEnabled(),
                target: PageId.ACCOUNT_WOOFY_WOOFZ_PPP_WOOFY,
              },
              {
                target: PageId.CHAT_UPSELL_WOOFZ_PPP_WOOFY,
              },
            ],
          }),
          ...(page.pageId === PageId.CHAT_UPSELL_WOOFZ_PPP_WOOFY && {
            NEXT_FLOW_STEP: PageId.CHAT_WOOFZ_PPP_WOOFY,
            SKIP_ONE_STEP: [
              {
                cond: isWoofyChatUpsellAfterAccountEnabled,
                target: PageId.DOWNLOAD_WOOFY_WOOFZ_PPP_WOOFY,
              },
              {
                target: PageId.ACCOUNT_WOOFY_WOOFZ_PPP_WOOFY,
              },
            ],
          }),
          ...(page.pageId === PageId.CHAT_WOOFZ_PPP_WOOFY && {
            NEXT_FLOW_STEP: [
              {
                cond: isWoofyChatUpsellAfterAccountEnabled,
                target: PageId.DOWNLOAD_WOOFY_WOOFZ_PPP_WOOFY,
              },
              {
                target: PageId.ACCOUNT_WOOFY_WOOFZ_PPP_WOOFY,
              },
            ],
            SKIP_ONE_STEP: [
              {
                cond: isWoofyChatUpsellAfterAccountEnabled,
                target: PageId.DOWNLOAD_WOOFY_WOOFZ_PPP_WOOFY,
              },
              {
                target: PageId.ACCOUNT_WOOFY_WOOFZ_PPP_WOOFY,
              },
            ],
          }),
        },
      },
    }),
    {},
  ),
  [PageId.ACCOUNT_WOOFY_WOOFZ_PPP_WOOFY]: {
    on: {
      NEXT_FLOW_STEP: [
        {
          cond: shouldShowChatUpsellAfterAccount,
          target: PageId.CHAT_UPSELL_WOOFZ_PPP_WOOFY,
        },
        {
          target: PageId.DOWNLOAD_WOOFY_WOOFZ_PPP_WOOFY,
        },
      ],
    },
  },
  [PageId.DOWNLOAD_WOOFY_WOOFZ_PPP_WOOFY]: {
    type: 'final',
  },
})
